import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { Hotel } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StorageKeys } from '~/atoms/StorageKeys'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  ExpandableTabChild,
  SiteNavigationExpandableTab,
} from '~/pages/Home/SiteNavigation/shared/SiteNavigationExpandableTab'

interface HotelsTabProps {
  isCollapsed?: boolean
  pathname: string
  canViewDashboard: boolean
  canViewSettings: boolean
  canViewScheduler: boolean
  canViewHistory: boolean
  onNavigate: (path: string) => void
}

const hotelsExpandedAtom = atomWithStorage<boolean>(
  StorageKeys.SiteNav + ':hotels-expanded',
  false,
)

export const HotelsTab = ({
  isCollapsed,
  pathname,
  canViewDashboard,
  canViewSettings,
  canViewScheduler,
  canViewHistory,
  onNavigate,
}: HotelsTabProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useAtom(hotelsExpandedAtom)

  const children = useMemo(() => {
    const items: ExpandableTabChild[] = []

    if (canViewDashboard) {
      items.push({
        id: 'hotels-dashboard',
        label: t('hotels.nav.dashboard', 'Dashboard'),
        path: ApplicationRoutes.HOTELS,
      })
    }

    if (canViewSettings) {
      items.push({
        id: 'hotels-settings',
        label: t('hotels.nav.settings', 'Settings'),
        path: ApplicationRoutes.HOTELS_SETTINGS,
      })
    }

    if (canViewScheduler) {
      items.push({
        id: 'hotels-wakeup-scheduling',
        label: t('hotels.nav.wakeupScheduling', 'Wake-Up Scheduling'),
        path: ApplicationRoutes.HOTELS_WAKEUP,
      })
    }

    if (canViewHistory) {
      items.push({
        id: 'hotels-wakeup-history',
        label: t('hotels.nav.wakeupHistory', 'Wake-Up History'),
        path: ApplicationRoutes.HOTELS_WAKEUP_HISTORY,
      })
    }

    return items
  }, [canViewDashboard, canViewSettings, canViewScheduler, canViewHistory, t])

  return (
    <SiteNavigationExpandableTab
      id="hotels"
      title={t('nav.hotels', 'Hotels')}
      icon={Hotel}
      isCollapsed={isCollapsed}
      isExpanded={isExpanded}
      isAnyChildActive={pathname.startsWith(ApplicationRoutes.HOTELS)}
      children={children}
      activeChildPath={pathname}
      onToggleExpand={() => setIsExpanded(!isExpanded)}
      onChildClick={onNavigate}
    />
  )
}
